import { createRoutine } from 'redux-saga-routines'
import { put, call, takeLatest, fork } from '@redux-saga/core/effects'
import * as authService from 'services/AuthService'
import {
  isExcludedFromFetchRedirect,
  PATHS,
  PRIVATE_PATHS,
  redirect
} from 'utils/paths'
import { toast } from 'react-toastify'
import { extractMessageFromErrorResponse, getApiErrors } from 'utils/errors'
import i18n from 'i18n'
import { dissoc } from 'ramda'
import LocalStorageService from 'services/LocalStorageService'
import { clearCart, KEYS } from 'utils/localStorage'
import * as bpService from 'services/BpService'
import localStorageService from 'services/LocalStorageService'
import CookieService from 'services/CookieService'
import { cacheUserForOffline, offlineLogin } from 'services/offlineAuth'
import { resetTaskPrefetchSchedule, scheduleTaskPrefetch } from 'services/offlinePrefetch'
import db from 'services/offlineDb'
import { updateSyncCountsRoutine, setOnlineStatusRoutine, isOnline as checkIsOnline } from 'ducks/offline/actions'
import { detachPushSubscription } from 'utils/pushNotifications'
import { homePathFor, rememberPageAtLogout, takeStoredRedirect } from 'utils/postLoginDestination'

export const registerUserRoutine = createRoutine('REGISTER_USER')
export const loginUserRoutine = createRoutine('LOGIN_USER')
export const setTwoFactorChallengeRoutine = createRoutine('SET_TWO_FACTOR_CHALLENGE')
export const verifyTwoFactorLoginRoutine = createRoutine('VERIFY_TWO_FACTOR_LOGIN')
export const logoutUserRoutine = createRoutine('LOGOUT_USER')
export const verifyEmailRoutine = createRoutine('VERIFY_EMAIL')
export const requestResetPasswordRoutine = createRoutine(
  'REQUEST_RESET_PASSWORD'
)
export const resetPasswordRoutine = createRoutine('RESET_PASSWORD')
export const fetchAuthUserRoutine = createRoutine('FETCH_AUTH_DETAILS')
export const changePasswordRoutine = createRoutine('CHANGE_PASSWORD')
export const clearAuthUserRoutine = createRoutine('CLEAR_AUTH_USER')
export const getAccreditationTypesRoutine = createRoutine(
  'GET_ACCREDITATION_TYPES'
)
export const getInvitationRoutine = createRoutine('GET_INVITATION')
export const clearLatestInvitationRoutine = createRoutine(
  'CLEAR_LATEST_INVITATION'
)
export const updateUserRoutine = createRoutine('UPDATE_USER')
export const updateUserLocalRoutine = createRoutine('UPDATE_USER_LOCAL')
export const getPublicApiTokenRoutine = createRoutine('GET_PUBLIC_API_TOKEN')
export const clearPublicApiTokenRoutine = createRoutine(
  'CLEAR_PUBLIC_API_TOKEN'
)
export const deleteAccountRoutine = createRoutine('DELETE_ACCOUNT')
export const getTwoFactorStatusRoutine = createRoutine('GET_TWO_FACTOR_STATUS')
export const startTwoFactorSetupRoutine = createRoutine('START_TWO_FACTOR_SETUP')
export const confirmTwoFactorSetupRoutine = createRoutine('CONFIRM_TWO_FACTOR_SETUP')
export const disableTwoFactorRoutine = createRoutine('DISABLE_TWO_FACTOR')
export const regenerateTwoFactorRecoveryCodesRoutine = createRoutine('REGENERATE_TWO_FACTOR_RECOVERY_CODES')

function* completeOnlineLogin(userData, token, payload = {}) {
  yield put(setOnlineStatusRoutine.success({ isOnline: true }))

  const lastLoggedUserId = LocalStorageService.get(KEYS.userId) || ''
  if (lastLoggedUserId !== userData.id) {
    clearCart()
    LocalStorageService.set(KEYS.userId, userData.id)
  }

  if (
    userData.type !== 'admin' &&
    !userData.twoFactorEnabled &&
    userData.twoFactorSetupPromptDue !== false
  ) {
    LocalStorageService.set(KEYS.token, token)
    yield put(loginUserRoutine.success(userData))
    redirect(PRIVATE_PATHS.twoFactorSetup)
    return
  }

  switch (userData.type) {
    case 'emergency_service':
      LocalStorageService.set(KEYS.token, token)
      yield put(loginUserRoutine.success(userData))
      redirect(takeStoredRedirect() || homePathFor(userData))
      break
    case 'admin':
      yield put(logoutUserRoutine())
      break
    default:
      LocalStorageService.set(KEYS.token, token)
      yield put(loginUserRoutine.success(userData))
      redirect(takeStoredRedirect() || homePathFor(userData))
  }

  if (userData.type !== 'admin' && payload.email && payload.password) {
    try {
      yield call(cacheUserForOffline, payload.email, payload.password, userData, token)
    } catch (cacheError) {
      console.warn('Failed to cache credentials for offline:', cacheError)
    }
    yield call(scheduleTaskPrefetch, { window: userData?.defaultTaskWindow || 'week' })
  }
}

function* registerUser({ payload }) {
  yield put(registerUserRoutine.request())
  try {
    yield call(authService.registerUser, payload)
    yield put(registerUserRoutine.success())
    redirect(PATHS.signupConfirm)
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(registerUserRoutine.failure(error))
  }
}

function* deleteAccount({ payload }) {
  yield put(deleteAccountRoutine.request())
  try {
    yield call(bpService.deleteUserAccount, payload)
    yield put(logoutUserRoutine())
    yield put(deleteAccountRoutine.success())
    toast.success(
      'Your request to delete account has been sent to the administrator'
    )
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(deleteAccountRoutine.failure(error))
  }
}

function* updateUser({ payload }) {
  const { __silent, ...attributes } = payload || {}
  yield put(updateUserRoutine.request())
  try {
    const { data } = yield call(authService.updateUser, attributes)
    yield put(updateUserRoutine.success(data.data))
    if (!__silent) toast.success('Your changes have been saved')
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(updateUserRoutine.failure(error))
  }
}

function* getInvitation({ payload }) {
  yield put(getInvitationRoutine.request())
  try {
    const result = yield call(authService.getUserInvitation, payload)
    yield put(getInvitationRoutine.success(result.data.data))
  } catch (error) {
    toast.error('Invitation not found')
    yield put(getInvitationRoutine.failure(error))
  }
}

function* clearLatestInvitation() {
  yield put(clearLatestInvitationRoutine.success())
}

let loginGeneration = 0

function* loginUser({ payload }) {
  loginGeneration += 1
  yield put(loginUserRoutine.request())
  try {
    if (!checkIsOnline()) {
      // OFFLINE LOGIN: verify against cached credentials
      const { userData, token } = yield call(offlineLogin, payload.email, payload.password)
      LocalStorageService.set(KEYS.token, token)
      yield put(loginUserRoutine.success(userData))
      yield put(updateSyncCountsRoutine())
      redirect(PRIVATE_PATHS.taskManagement)
      LocalStorageService.remove(KEYS.redirectPath)
      return
    }

    // ONLINE LOGIN: existing flow
    LocalStorageService.remove(KEYS.token)
    const result = yield call(authService.loginUser, payload)
    if (result.data.data?.twoFactorToken) {
      yield put(setTwoFactorChallengeRoutine.success({
        providers: result.data.data.providers,
        email: payload.email,
        twoFactorToken: result.data.data.twoFactorToken
      }))
      yield put(loginUserRoutine.failure())
      return
    }

    const authToken = yield call(authService.getApiToken)
    const userData = result.data.data
    yield call(completeOnlineLogin, userData, authToken.data.data.token, payload)
  } catch (error) {
    // Network error (DevTools offline, airplane mode, etc.) — try offline login as fallback
    const isNetworkError = error?.message === 'Network Error' || error?.code === 'ERR_NETWORK' || !checkIsOnline()
    if (isNetworkError) {
      // Update Redux so banner/icons reflect offline state
      yield put(setOnlineStatusRoutine.success({ isOnline: false }))
      try {
        const { userData, token } = yield call(offlineLogin, payload.email, payload.password)
        LocalStorageService.set(KEYS.token, token)
        yield put(loginUserRoutine.success(userData))
        yield put(updateSyncCountsRoutine())
        redirect(PRIVATE_PATHS.taskManagement)
        LocalStorageService.remove(KEYS.redirectPath)
        return
      } catch (offlineError) {
        toast.error(offlineError.message || 'Cannot log in offline. No cached credentials found.')
        yield put(loginUserRoutine.failure(offlineError))
        return
      }
    }
    toast.error(extractMessageFromErrorResponse(error))
    yield put(loginUserRoutine.failure(error))
  }
}

function* verifyTwoFactorLogin({ payload }) {
  yield put(verifyTwoFactorLoginRoutine.request())
  try {
    LocalStorageService.remove(KEYS.token)
    const result = yield call(authService.verifyTwoFactorLogin, payload)
    let authToken
    try {
      authToken = yield call(authService.getApiToken)
    } catch (error) {
      toast.error('We could not finish signing you in. Please sign in again.')
      yield put(setTwoFactorChallengeRoutine.success(null))
      yield put(verifyTwoFactorLoginRoutine.failure(error))
      return
    }
    const userData = result.data.data
    yield call(completeOnlineLogin, userData, authToken.data.data.token, payload)
    yield put(verifyTwoFactorLoginRoutine.success(userData))
  } catch (error) {
    toast.error(extractMessageFromErrorResponse(error))
    yield put(verifyTwoFactorLoginRoutine.failure(error))
  }
}

function* getTwoFactorStatus() {
  yield put(getTwoFactorStatusRoutine.request())
  try {
    const result = yield call(authService.getTwoFactorStatus)
    yield put(getTwoFactorStatusRoutine.success(result.data.data))
  } catch (error) {
    yield put(getTwoFactorStatusRoutine.failure(error))
  }
}

function* startTwoFactorSetup() {
  yield put(startTwoFactorSetupRoutine.request())
  try {
    const result = yield call(authService.startTwoFactorSetup)
    yield put(startTwoFactorSetupRoutine.success(result.data.data))
  } catch (error) {
    toast.error(extractMessageFromErrorResponse(error))
    yield put(startTwoFactorSetupRoutine.failure(error))
  }
}

function* confirmTwoFactorSetup({ payload }) {
  yield put(confirmTwoFactorSetupRoutine.request())
  try {
    const result = yield call(authService.confirmTwoFactorSetup, payload)
    yield put(confirmTwoFactorSetupRoutine.success(result.data.data))
    toast.success('Two-factor authentication has been enabled.')
  } catch (error) {
    toast.error(extractMessageFromErrorResponse(error))
    yield put(confirmTwoFactorSetupRoutine.failure(error))
  }
}

function* regenerateTwoFactorRecoveryCodes({ payload }) {
  yield put(regenerateTwoFactorRecoveryCodesRoutine.request())
  const { onSuccess, ...requestPayload } = payload || {}
  try {
    const result = yield call(authService.regenerateTwoFactorRecoveryCodes, requestPayload)
    yield put(regenerateTwoFactorRecoveryCodesRoutine.success(result.data.data))
    toast.success('Recovery codes have been regenerated.')
    onSuccess?.(result.data.data.recoveryCodes)
  } catch (error) {
    toast.error(extractMessageFromErrorResponse(error))
    yield put(regenerateTwoFactorRecoveryCodesRoutine.failure(error))
  }
}

function* disableTwoFactor({ payload }) {
  yield put(disableTwoFactorRoutine.request())
  const { onSuccess, ...requestPayload } = payload || {}
  try {
    const result = yield call(authService.disableTwoFactor, requestPayload)
    yield put(disableTwoFactorRoutine.success(result.data.data))
    toast.success('Two-factor authentication has been disabled.')
    onSuccess?.()
  } catch (error) {
    toast.error(extractMessageFromErrorResponse(error))
    yield put(disableTwoFactorRoutine.failure(error))
  }
}

function* getPublicApiToken() {
  yield put(getPublicApiTokenRoutine.request())
  try {
    const { data } = yield call(authService.getPublicApiToken)
    yield put(getPublicApiTokenRoutine.success(data.data.token))
  } catch (e) {
    console.error(e)
    yield put(getPublicApiTokenRoutine.failure())
  }
}

function* clearPublicApiToken() {
  yield put(clearPublicApiTokenRoutine.success())
}

function* logoutUser({ payload }) {
  yield put(logoutUserRoutine.request())
  rememberPageAtLogout()
  resetTaskPrefetchSchedule()
  const tokenAtStart = localStorageService.get(KEYS.token)
  const generationAtStart = loginGeneration
  const signedInMeanwhile = () =>
    loginGeneration !== generationAtStart || localStorageService.get(KEYS.token) !== tokenAtStart
  try {
    if (checkIsOnline()) {
      yield call(detachPushSubscription)
      if (signedInMeanwhile()) return
      yield call(authService.logoutUser)
    }
    if (signedInMeanwhile()) return
    yield put(logoutUserRoutine.success())
    redirect(PATHS.login)
    localStorageService.remove(KEYS.token)
    // Clear all BP-related cookies on logout
    CookieService.clearAllBpCookies()

    payload && typeof payload.callback === 'function' && payload.callback()
  } catch (err) {
    console.error(err)
    payload && typeof payload.callback === 'function' && payload.callback()
    yield put(logoutUserRoutine.failure())
  }
}

function* clearAuthUser() {
  yield put(clearAuthUserRoutine.request())
  try {
    yield put(clearAuthUserRoutine.success())
    // Clear all BP-related cookies on session expiry
    CookieService.clearAllBpCookies()
    setTimeout(() => redirect(PATHS.login), 100)
  } catch (err) {
    console.error(err)
    yield put(clearAuthUserRoutine.failure())
  }
}

function* fetchAuthUser() {
  yield put(fetchAuthUserRoutine.request())
  try {
    if (!checkIsOnline()) {
      // OFFLINE: use cached user data from IndexedDB
      const cachedAuth = yield call(() => db.auth.toCollection().first())
      if (cachedAuth?.userData) {
        yield put(fetchAuthUserRoutine.success(cachedAuth.userData))
        return
      }
    }
    const result = yield call(authService.fetchAuthUser)
    yield put(setOnlineStatusRoutine.success({ isOnline: true }))
    yield put(fetchAuthUserRoutine.success(result.data.data))
  } catch (error) {
    // Network error while offline — try IDB fallback
    const isNetworkError = error?.message === 'Network Error' || error?.code === 'ERR_NETWORK' || !checkIsOnline()
    if (isNetworkError) {
      yield put(setOnlineStatusRoutine.success({ isOnline: false }))
      try {
        const cachedAuth = yield call(() => db.auth.toCollection().first())
        if (cachedAuth?.userData) {
          yield put(fetchAuthUserRoutine.success(cachedAuth.userData))
          return
        }
      } catch (e) {
        // IDB failed too, fall through
      }
    }
    if (!isExcludedFromFetchRedirect(window.location.pathname)) {
      rememberPageAtLogout()
      yield put(clearAuthUserRoutine())
      yield put(logoutUserRoutine())
    }
    console.error(error)
    yield put(fetchAuthUserRoutine.failure(error))
  }
}

function* getAccreditationTypes() {
  yield put(getAccreditationTypesRoutine.request())
  try {
    const result = yield call(authService.getAccreditationTypes)
    yield put(getAccreditationTypesRoutine.success(result.data.data))
  } catch (error) {
    console.error(error)
    yield put(getAccreditationTypesRoutine.failure(error))
  }
}

function* changePassword({ payload }) {
  yield put(changePasswordRoutine.request())
  const { clearValues } = payload

  try {
    yield call(authService.changePassword, dissoc('clearValues', payload))
    yield put(changePasswordRoutine.success())
    toast.success(i18n.t('passwordReset.alerts.passwordChanged'))
    typeof clearValues === 'function' && clearValues()
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(changePasswordRoutine.failure(error))
  }
}

function* requestResetPassword({ payload }) {
  yield put(requestResetPasswordRoutine.request())
  try {
    yield call(authService.requestResetPassword, payload)
    yield put(requestResetPasswordRoutine.success())
    redirect(PATHS.login)
    toast.success(i18n.t('passwordReset.alerts.requestResetSuccess'))
  } catch (error) {
    console.error(error)
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(requestResetPasswordRoutine.failure(error))
  }
}

function* resetPassword({ payload }) {
  yield put(resetPasswordRoutine.request())
  try {
    yield call(authService.resetPassword, payload)
    yield put(resetPasswordRoutine.success())
    toast.success(i18n.t('passwordReset.alerts.passwordChanged'))
    redirect(PATHS.login)
  } catch (error) {
    console.error(error)
    toast.error(extractMessageFromErrorResponse(error))
    yield put(resetPasswordRoutine.failure(error))
  }
}

function* verifyEmail({ payload }) {
  const { queryParams, setStatus } = payload
  yield put(verifyEmailRoutine.request())
  try {
    yield call(authService.verifyEmail, queryParams)
    yield put(verifyEmailRoutine.success())
    setStatus('success')
    setTimeout(() => redirect(PATHS.login), 5000)
  } catch (error) {
    setStatus('error')
    setTimeout(() => redirect(PATHS.login), 5000)
    console.error(error)
    yield put(verifyEmailRoutine.failure(error))
  }
}

// WATCHERS

export function* registerUserWatcher() {
  yield takeLatest(registerUserRoutine.TRIGGER, registerUser)
}

export function* updateUserWatcher() {
  yield takeLatest(updateUserRoutine.TRIGGER, updateUser)
}

export function* loginUserWatcher() {
  yield takeLatest(loginUserRoutine.TRIGGER, loginUser)
}

export function* verifyTwoFactorLoginWatcher() {
  yield takeLatest(verifyTwoFactorLoginRoutine.TRIGGER, verifyTwoFactorLogin)
}

export function* logoutUserWatcher() {
  yield takeLatest(logoutUserRoutine.TRIGGER, logoutUser)
}

export function* fetchAuthUserWatcher() {
  yield takeLatest(fetchAuthUserRoutine.TRIGGER, fetchAuthUser)
}

export function* verifyEmailWatcher() {
  yield takeLatest(verifyEmailRoutine.TRIGGER, verifyEmail)
}

export function* requestResetPasswordWatcher() {
  yield takeLatest(requestResetPasswordRoutine.TRIGGER, requestResetPassword)
}

export function* resetPasswordWatcher() {
  yield takeLatest(resetPasswordRoutine.TRIGGER, resetPassword)
}

export function* changePasswordWatcher() {
  yield takeLatest(changePasswordRoutine.TRIGGER, changePassword)
}

export function* clearAuthUserWatcher() {
  yield takeLatest(clearAuthUserRoutine.TRIGGER, clearAuthUser)
}

export function* getAccreditationTypesWatcher() {
  yield takeLatest(getAccreditationTypesRoutine.TRIGGER, getAccreditationTypes)
}

export function* getInvitationWatcher() {
  yield takeLatest(getInvitationRoutine.TRIGGER, getInvitation)
}

export function* clearLatestInvitationWatcher() {
  yield takeLatest(clearLatestInvitationRoutine.TRIGGER, clearLatestInvitation)
}

export function* getPublicApiTokenWatcher() {
  yield takeLatest(getPublicApiTokenRoutine.TRIGGER, getPublicApiToken)
}

export function* clearPublicApiTokenWatcher() {
  yield takeLatest(clearPublicApiTokenRoutine.TRIGGER, clearPublicApiToken)
}

export function* deleteAccountWatcher() {
  yield takeLatest(deleteAccountRoutine.TRIGGER, deleteAccount)
}

export function* getTwoFactorStatusWatcher() {
  yield takeLatest(getTwoFactorStatusRoutine.TRIGGER, getTwoFactorStatus)
}

export function* startTwoFactorSetupWatcher() {
  yield takeLatest(startTwoFactorSetupRoutine.TRIGGER, startTwoFactorSetup)
}

export function* confirmTwoFactorSetupWatcher() {
  yield takeLatest(confirmTwoFactorSetupRoutine.TRIGGER, confirmTwoFactorSetup)
}

export function* disableTwoFactorWatcher() {
  yield takeLatest(disableTwoFactorRoutine.TRIGGER, disableTwoFactor)
}

export function* regenerateTwoFactorRecoveryCodesWatcher() {
  yield takeLatest(regenerateTwoFactorRecoveryCodesRoutine.TRIGGER, regenerateTwoFactorRecoveryCodes)
}

// SAGAS
export const authSagas = [
  fork(loginUserWatcher),
  fork(verifyTwoFactorLoginWatcher),
  fork(logoutUserWatcher),
  fork(clearLatestInvitationWatcher),
  fork(registerUserWatcher),
  fork(updateUserWatcher),
  fork(getPublicApiTokenWatcher),
  fork(clearPublicApiTokenWatcher),
  fork(fetchAuthUserWatcher),
  fork(getInvitationWatcher),
  fork(verifyEmailWatcher),
  fork(requestResetPasswordWatcher),
  fork(resetPasswordWatcher),
  fork(changePasswordWatcher),
  fork(getAccreditationTypesWatcher),
  fork(clearAuthUserWatcher),
  fork(deleteAccountWatcher),
  fork(getTwoFactorStatusWatcher),
  fork(startTwoFactorSetupWatcher),
  fork(confirmTwoFactorSetupWatcher),
  fork(disableTwoFactorWatcher),
  fork(regenerateTwoFactorRecoveryCodesWatcher)
]
